import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton, LinkButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import Image from '@atlaskit/image';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onNonGenerativeAIAnswerResultShown } from '../../../ai-answer-dialog/analytics';
import {
	SainAction,
	useAnalytics,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics';
import LocationDark from '../assets/location-dark.svg';
import LocationLight from '../assets/location-light.svg';
import { StandardActionsKnowledgeCard } from '../standard-cards/StandardActionsKnowledgeCard';

const messages = defineMessages({
	knowledge_card_location_category: {
		id: 'search_ai_knowledge_card_location_category',
		defaultMessage: 'Location',
		description: 'Category header for location knowledge card',
	},
	knowledge_card_location_google_maps_action: {
		id: 'search_ai_knowledge_card_location_google_maps_action',
		defaultMessage: 'Show on Google Maps',
		description: 'Button to show in Google',
	},
});

const generateGoogleMapsLink = (location: string) => {
	const encodedAddress = encodeURIComponent(location);
	return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
};

const copyLocation = async (location: string) => {
	await navigator.clipboard.writeText(location);
};

export const LocationCard = ({ location }: { location: string }) => {
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const { brand, commonAttributes } = useAIAnswerContext();
	const { answerString, query, queryHash, searchSessionId } = commonAttributes;

	const onClickGoogleMaps = useCallback(() => {
		fireAnalyticsEvent(
			onNonGenerativeAIAnswerResultShown({
				answerString,
				brand,
				query,
				queryHash,
				searchSessionId,
				source: 'location_knowledge_card',
			}),
		);
		fireSAINUsageEvent({ metCriteria: SainAction.SHOW_ON_GOOGLE_MAPS_CLICKED });
	}, [
		answerString,
		brand,
		fireAnalyticsEvent,
		fireSAINUsageEvent,
		query,
		queryHash,
		searchSessionId,
	]);

	const googleMapsLink = generateGoogleMapsLink(location);

	return (
		<StandardActionsKnowledgeCard
			name="smart-answer-location-knowledge-card"
			category={formatMessage(messages.knowledge_card_location_category)}
			primaryDescription={location}
			actions={[
				<LinkButton href={googleMapsLink} onClick={onClickGoogleMaps}>
					{formatMessage(messages.knowledge_card_location_google_maps_action)}
				</LinkButton>,
				<IconButton icon={CopyIcon} label="copy" onClick={() => copyLocation(location)} />,
			]}
			media={<Image src={LocationLight} srcDark={LocationDark} />}
			size="full"
		/>
	);
};
